import { useCallback, useEffect, useRef, useState } from 'react';

export function useBrowserSpeechRecognition() {
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const finalTranscriptRef = useRef('');
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');

  const browserSupportsSpeechRecognition = Boolean(
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)
  );

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      return undefined;
    }

    const SpeechRecognitionConstructor = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionConstructor();
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let nextFinalTranscript = finalTranscriptRef.current;
      let nextInterimTranscript = '';

      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const nextChunk = result[0]?.transcript || '';

        if (result.isFinal) {
          nextFinalTranscript = `${nextFinalTranscript} ${nextChunk}`.trim();
        } else {
          nextInterimTranscript = `${nextInterimTranscript} ${nextChunk}`.trim();
        }
      }

      finalTranscriptRef.current = nextFinalTranscript;
      setTranscript(nextFinalTranscript);
      setInterimTranscript(nextInterimTranscript);
    };

    recognition.onend = () => {
      if (!isListeningRef.current) {
        return;
      }

      try {
        recognition.start();
      } catch (error) {
        console.warn('Speech recognition restart skipped:', error);
      }
    };

    recognition.onerror = (event) => {
      if (event?.error === 'not-allowed' || event?.error === 'service-not-allowed') {
        isListeningRef.current = false;
      }
    };

    recognitionRef.current = recognition;

    return () => {
      isListeningRef.current = false;
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [browserSupportsSpeechRecognition]);

  const resetTranscript = useCallback(() => {
    finalTranscriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  const startListening = useCallback((options = {}) => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      return;
    }

    recognition.continuous = options.continuous ?? false;
    recognition.lang = options.language || 'en-US';
    isListeningRef.current = true;

    try {
      recognition.start();
    } catch (error) {
      if (error?.name !== 'InvalidStateError') {
        console.error('Unable to start speech recognition:', error);
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    const recognition = recognitionRef.current;
    isListeningRef.current = false;

    if (!recognition) {
      return;
    }

    recognition.stop();
  }, []);

  return {
    transcript,
    interimTranscript,
    resetTranscript,
    startListening,
    stopListening,
    browserSupportsSpeechRecognition,
  };
}